import { AutomationHistoryWithAirportStatusEnum, AutomationTrip } from '@/models/gen/graphql';
import { Dayts, Schema, createNotification } from '@/utils';
import searchAutomationTrip, { searchAutomationTripPreview } from '@/features/Automation/api/services/searchAutomationTrip';

import { DATE_FE_FORMAT } from '@/constants';
import { InitComponentStateType } from '@/state';
import { Toast } from '@/models';
import runScheduleAutomation from '@/features/Automation/api/services/runScheduleAutomation';
import searchAutomationHistoryWithAirport from '@/features/Automation/api/services/searchAutomationHistoryWithAirport';

export namespace AutomationTripsTypes {
  export type Filters = {
    from: string;
    to: string;
    market: string;
    isPreview: boolean;
  };
  export const initFilters: Filters = {
    from:
      new Dayts().hour() < 4
        ? new Dayts().startOf('day').format(DATE_FE_FORMAT)
        : new Dayts().add(1, 'day').startOf('day').format(DATE_FE_FORMAT),
    to:
      new Dayts().hour() < 4
        ? new Dayts().add(2, 'days').endOf('day').format(DATE_FE_FORMAT)
        : new Dayts().add(3, 'days').endOf('day').format(DATE_FE_FORMAT),
    market: undefined,
    isPreview: false,
  };
  export type Table = {
    sorting: {
      column: string | undefined;
      direction: 'asc' | 'desc' | undefined;
    };
  };
  export const initTable: Table = {
    sorting: {
      column: 'date',
      direction: 'desc',
    },
  };
  export type State = {
    trips: AutomationTrip[];
    total: number;
    selected: string[];
    loading: boolean;
    processing: boolean | string;
    filters: Filters;
    table: Table;
    fetch: (automationHistoryId?: string) => Promise<void>;
    preview: (filters?: Filters) => Promise<void>;
    runAutomation: () => Promise<void>;
    pollAutomation: () => Promise<void>;
    pollStatus: AutomationHistoryWithAirportStatusEnum | null;
    onSelect: (id: string) => void;
    onReset: (form) => () => void;
  };
  export const initState: InitComponentStateType<State> = (setState, getState) => {
    const fetch = async (automationHistoryId: string): Promise<void> => {
      try {
        if (!automationHistoryId) return;
        const trips = await searchAutomationTrip(automationHistoryId);
        setState((current) => ({
          ...current,
          trips,
          total: trips.length,
          selected: [],
          filters: { ...current.filters, isPreview: false },
        }));
      } catch (err) {
        console.error('Error fetching trips:', err);
      } finally {
        setState((current) => ({ ...current, loading: false }));
      }
    };
    const preview = async (filters: Filters): Promise<void> => {
      try {
        const { from, to, market: marketString } = { ...initFilters, ...filters };
        const market = marketString?.split(',')?.filter(Boolean);
        if (!market?.length) return;
        setState((current) => ({
          ...current,
          filters: { ...filters, isPreview: current.filters.isPreview },
          loading: true,
        }));
        const trips = await searchAutomationTripPreview(from, to, market);
        setState((current) => ({ ...current, trips, total: trips.length, selected: [], filters: { ...current.filters, isPreview: true } }));
      } catch (err) {
        console.error('Error fetching trips:', err);
      } finally {
        setState((current) => ({ ...current, loading: false }));
      }
    };
    const runAutomation = async (): Promise<void> => {
      try {
        const { from, to, market: marketString } = getState().state.filters;
        const market = marketString?.split(',')?.filter(Boolean);
        if (!market?.length) return;
        setState((current) => ({ ...current, processing: true }));
        console.log('Running automation with:', { from, to, market });
        const res = await runScheduleAutomation(from, to, market);
        console.log('Automation result:', res);
        setState((current) => ({ ...current, processing: res?.id, pollStatus: res?.status ?? null }));
      } catch {
        setState((current) => ({ ...current, processing: false, pollStatus: null }));
      }
    };
    const pollAutomation = async (): Promise<void> => {
      try {
        const { processing } = getState().state;
        if (typeof processing !== 'string') return;
        console.log('Polling automation for:', processing);
        const res = await searchAutomationHistoryWithAirport(processing);
        setState((current) => ({ ...current, pollStatus: res?.status ?? null }));
        console.log('History result:', res);
        switch (res?.status) {
          case AutomationHistoryWithAirportStatusEnum.Completed: {
            createNotification('Automation completed successfully.', Toast.Type.SUCCESS, 'Automation Completed');
            await fetch(processing);
            setState((current) => ({ ...current, processing: false, pollStatus: res?.status ?? null }));
            break;
          }
          case AutomationHistoryWithAirportStatusEnum.Failed: {
            createNotification('Something went wrong. Please try again.', Toast.Type.DANGER, 'Automation Failed');
            setState((current) => ({ ...current, processing: false, pollStatus: res?.status ?? null }));
            break;
          }
          default: {
            console.log('Unhandled automation status:', res?.status);
          }
        }
      } catch {
        setState((current) => ({ ...current, processing: false, pollStatus: null }));
      }
    };

    return {
      trips: [],
      total: 0,
      selected: [],
      loading: false,
      processing: false,
      filters: initFilters,
      table: initTable,
      fetch,
      preview,
      runAutomation,
      pollAutomation,
      pollStatus: null,
      onSelect: (id: string): void => {
        setState((current) => ({
          ...current,
          selected: current.selected.includes(id) ? current.selected.filter((val) => val !== id) : [...current.selected, id],
        }));
      },
      onReset: (form) => (): void => {
        setState((current) => ({ ...current, filters: { from: '', to: '', market: '', isPreview: false }, trips: [] }));
        form.setValue('from', '');
        form.setValue('to', '');
        form.setValue('market', '');
        form.trigger();
      },
    };
  };
  export const validator = Schema((z) =>
    z
      .object({
        from: z.string().nonempty(),
        to: z.string().nonempty(),
        market: z.string().nonempty(),
      })
      .superRefine((data, ctx) => {
        const today = new Dayts().startOf('day');
        const fromDate = new Dayts(data.from).startOf('day');
        const toDate = new Dayts(data.to).startOf('day');
        const maxToDate = fromDate.clone().add(2, 'days');

        const fromInvalid = fromDate.isBefore(today);
        const rangeInvalid = toDate.isBefore(fromDate) || toDate.isAfter(maxToDate);

        if (fromInvalid) {
          ctx.addIssue({
            path: ['from'],
            message: 'From date must be the current date or a future date.',
            code: 'custom',
          });
        }
        if (rangeInvalid) {
          ctx.addIssue({
            path: ['to'],
            message: 'To date must be on or after the from date and no more than 2 days later.',
            code: 'custom',
          });
        }
      })
  );
  export const POLLING_TIMES: Record<AutomationHistoryWithAirportStatusEnum, number | null> = {
    [AutomationHistoryWithAirportStatusEnum.AssigningDrivers]: 3000,
    [AutomationHistoryWithAirportStatusEnum.AssigningDriversQueued]: 5000,
    [AutomationHistoryWithAirportStatusEnum.Completed]: null,
    [AutomationHistoryWithAirportStatusEnum.Failed]: null,
    [AutomationHistoryWithAirportStatusEnum.InQueue]: 3000,
    [AutomationHistoryWithAirportStatusEnum.ProcessingData]: 7000,
    [AutomationHistoryWithAirportStatusEnum.ReadyToAssign]: 5000,
  };
}
